import { useEffect, useState } from "react";
import { CaretDown, CaretUp, PenNib, Printer } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

export default function SignatureChecklistCard() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState("");

  useEffect(() => {
    api.get("/training/signature-checklist").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);
  if (!data) return null;

  const printSheet = (t) => {
    const w = window.open("", "_blank");
    if (!w) { toast.error("Pop-up blocked — allow pop-ups to print"); return; }
    const rows = t.needs_signature.map((p) => `
      <tr><td>${p.name}</td><td>${p.last_done || ""}</td><td></td><td></td></tr>`).join("");
    w.document.write(`<!doctype html><html><head><title>Sign-off sheet — ${t.title}</title>
      <style>
        body{font-family:Georgia,serif;margin:32px;color:#1c1917}
        h1{font-size:20px;margin:0} p.sub{font-size:12px;color:#57534e;margin:4px 0 16px}
        table{width:100%;border-collapse:collapse} th,td{border:1px solid #a8a29e;padding:8px;font-size:13px;text-align:left}
        th{background:#f5f5f4;font-size:11px;text-transform:uppercase;letter-spacing:.05em}
        td{height:26px} .foot{margin-top:18px;font-size:11px;color:#78716c}
      </style></head><body>
      <h1>Training sign-off sheet — ${t.title}</h1>
      <p class="sub">Silver Home Care · These staff have this training on record. Each person signs and dates to confirm they completed it.</p>
      <table><thead><tr><th>Employee</th><th>Completion date on record</th><th>Employee signature</th><th>Date signed</th></tr></thead>
      <tbody>${rows}</tbody></table>
      <p class="foot">Only collect signatures for training that actually happened on the date shown. If someone never did this training, they need the training — not a signature.</p>
      </body></html>`);
    w.document.close();
    w.focus();
    w.onload = () => w.print();
  };

  return (
    <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm md:col-span-2" data-testid="signature-checklist-card">
      <div className="flex items-center gap-2">
        <PenNib size={16} className="text-primary" />
        <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">
          Signature checklist <span className="text-[#8a5d14]">({data.total_needs_signature} did it, need signature · {data.total_needs_training} never on record)</span>
        </p>
      </div>
      <p className="mt-1 text-xs text-stone-400">Did it but the signed sheet isn't on file → collect a signature. Never on record → they need the training itself. Print a sign-off sheet per training.</p>
      <div className="mt-3 grid max-h-80 grid-cols-1 gap-2 overflow-y-auto sm:grid-cols-2">
        {(data.topics || []).map((t) => (
          <div key={t.topic_id} className="rounded-lg border border-stone-200" data-testid={`sig-topic-${t.topic_id}`}>
            <button type="button" onClick={() => setOpen(open === t.topic_id ? "" : t.topic_id)}
              className="flex w-full items-center justify-between px-3 py-2 text-left" data-testid={`sig-topic-toggle-${t.topic_id}`}>
              <p className="text-sm font-medium text-stone-800">{t.title}{t.off_list && <span className="ml-2 rounded-full border border-stone-300 bg-stone-100 px-2 py-0.5 text-[9px] font-semibold text-stone-500" data-testid={`sig-offlist-${t.topic_id}`}>not a current topic</span>}</p>
              <span className="flex items-center gap-1.5">
                {t.needs_signature.length > 0 && <span className="rounded-full bg-[#B7791F]/10 px-1.5 py-0.5 text-[10px] font-semibold text-[#8a5d14]">{t.needs_signature.length} sign</span>}
                {t.needs_training.length > 0 && <span className="rounded-full bg-[#C0392B]/10 px-1.5 py-0.5 text-[10px] font-semibold text-[#C0392B]">{t.needs_training.length} never</span>}
                {open === t.topic_id ? <CaretUp size={14} className="text-stone-400" /> : <CaretDown size={14} className="text-stone-400" />}
              </span>
            </button>
            {open === t.topic_id && (
              <div className="max-h-48 space-y-1.5 overflow-y-auto border-t border-stone-100 px-3 py-2">
                {t.needs_signature.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between">
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-[#8a5d14]">Did it — needs signature</p>
                      <button onClick={() => printSheet(t)} className="inline-flex items-center gap-1 text-[10px] font-medium text-primary hover:underline" data-testid={`sig-print-${t.topic_id}`}>
                        <Printer size={11} /> Print sign-off sheet
                      </button>
                    </div>
                    {t.needs_signature.map((p) => (
                      <p key={p.user_id} className="text-xs text-stone-700" data-testid={`sig-person-${t.topic_id}-${p.user_id}`}>
                        {p.name} <span className="text-stone-400">· done {p.last_done}</span>
                      </p>
                    ))}
                  </div>
                )}
                {t.needs_training.length > 0 && (
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-[#C0392B]">Never on record — needs the training</p>
                    {t.needs_training.map((p) => (
                      <p key={p.user_id} className="text-xs text-stone-700" data-testid={`sig-never-${t.topic_id}-${p.user_id}`}>{p.name}</p>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
        {!(data.topics || []).length && <p className="text-xs font-medium text-[#1e8449]">Every completion on record has a signed sheet.</p>}
      </div>
    </div>
  );
}
